import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fmtPKR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import HardDeleteDialog from "../components/HardDeleteDialog";
import {
  ArrowLeft, Phone, MapPin, IdCard, ShieldCheck, Users, FileText, Trash2, Save, Pencil, Briefcase, Wallet, Mail,
} from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value, tone = "slate" }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-md border p-3 ${colors[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-60">{label}</div>
      <div className="font-display text-xl font-bold tabular">{value}</div>
    </div>
  );
}

function initials(name = "") {
  return name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
}

export default function GuarantorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = () => api.get(`/guarantors/${id}`).then((r) => {
    setData(r.data);
    setForm(r.data.guarantor);
  }).catch((err) => toast.error(formatApiError(err)));

  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;
  const g = edit ? form : data.guarantor;
  const customers = data.customers || [];

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/guarantors/${id}`, { ...form });
      toast.success("Guarantor updated");
      setEdit(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <div className="space-y-6" data-testid="guarantor-profile-page">
      <div className="flex items-center gap-3">
        <Link to="/guarantors" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Guarantors
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 flex items-start gap-4 flex-wrap">
        <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-emerald-200 overflow-hidden flex items-center justify-center text-slate-500 font-bold text-2xl">
          {g.documents?.photo ? (
            <img src={g.documents.photo} alt={g.name} className="w-full h-full object-cover" />
          ) : (
            initials(g.name)
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-display text-2xl font-bold text-slate-900" data-testid="guarantor-name">{g.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">Central guarantor pool · ID {g.id?.slice(0, 8)}</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><IdCard className="w-4 h-4 text-slate-400" /> <span className="tabular">{g.cnic || "—"}</span></div>
            <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-slate-400" /> <span className="tabular">{g.mobile || "—"}</span></div>
            <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400" /> {g.address || g.residence_address || "—"}</div>
            <div className="flex items-center gap-2 text-slate-700"><Briefcase className="w-4 h-4 text-slate-400" /> {g.occupation || g.designation || "—"}{g.city ? ` · ${g.city}` : ""}</div>
            {g.father_husband_name && <div className="flex items-center gap-2 text-slate-700"><Users className="w-4 h-4 text-slate-400" /> {g.father_husband_name}</div>}
            {g.monthly_income != null && g.monthly_income !== "" && <div className="flex items-center gap-2 text-slate-700"><Wallet className="w-4 h-4 text-slate-400" /> {fmtPKR(g.monthly_income)} / mo</div>}
          </div>
        </div>
        <div className="flex gap-2">
          {!edit ? (
            <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="guarantor-edit-btn">
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="guarantor-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="guarantor-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Linked Customers" value={g.customers_linked || 0} tone="sky" />
        <StatTile label="Active Loans" value={g.active_loans || 0} tone="emerald" />
        <StatTile label="Closed Loans" value={g.closed_loans || 0} tone="slate" />
        <StatTile label="Outstanding" value={fmtPKR(g.outstanding_amount || 0)} tone="amber" />
      </div>

      {edit && (
        <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="guarantor-edit-form">
          <div className="md:col-span-2 font-display font-semibold text-slate-900">Edit Profile</div>
          <div className="space-y-1"><Label>Name</Label><Input value={form.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="guarantor-edit-name" /></div>
          <div className="space-y-1"><Label>Relationship</Label><Input value={form.relationship || ""} onChange={(e) => set({ relationship: e.target.value })} /></div>
          <div className="space-y-1"><Label>CNIC</Label><Input value={form.cnic || ""} onChange={(e) => set({ cnic: e.target.value })} data-testid="guarantor-edit-cnic" /></div>
          <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile || ""} onChange={(e) => set({ mobile: e.target.value })} /></div>
          <div className="space-y-1"><Label>Father / Husband Name</Label><Input value={form.father_husband_name || ""} onChange={(e) => set({ father_husband_name: e.target.value })} /></div>
          <div className="space-y-1"><Label>City</Label><Input value={form.city || ""} onChange={(e) => set({ city: e.target.value })} /></div>
          <div className="space-y-1"><Label>Occupation</Label><Input value={form.occupation || ""} onChange={(e) => set({ occupation: e.target.value })} /></div>
          <div className="space-y-1"><Label>Monthly Income (PKR)</Label><Input type="number" value={form.monthly_income ?? ""} onChange={(e) => set({ monthly_income: e.target.value === "" ? null : Number(e.target.value) })} /></div>
          <div className="space-y-1 md:col-span-2"><Label>Address</Label><Input value={form.address || form.residence_address || ""} onChange={(e) => set({ address: e.target.value, residence_address: e.target.value })} /></div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 font-display font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-600" /> Linked Customers ({customers.length})
        </div>
        {customers.length === 0 ? (
          <div className="px-4 py-10 text-center text-slate-500 text-sm">No customers linked to this guarantor yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Customer</th>
                  <th className="px-4 py-2 text-left">CNIC</th>
                  <th className="px-4 py-2 text-left">Mobile</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-right">Active</th>
                  <th className="px-4 py-2 text-right">Closed</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`guarantor-linked-cust-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 tabular text-slate-600">{c.cnic || "—"}</td>
                    <td className="px-4 py-3 tabular text-slate-600">{c.mobile || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {c.role.replace("guarantor", "G ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular text-emerald-700 font-bold">{c.active_loans}</td>
                    <td className="px-4 py-3 text-right tabular text-slate-500">{c.closed_loans}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/customers/${c.id}`} className="text-emerald-700 hover:text-emerald-900 font-semibold text-xs">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <HardDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Guarantor"
        targetLabel={g.name}
        endpoint={`/guarantors/${id}`}
        description="Guarantor record permanently delete ho jayega. Active loans hain to delete block ho jayega — pehle close karein. Customers se link automatically detach ho jayega."
        onDeleted={() => { toast.success("Guarantor deleted."); navigate("/guarantors"); }}
      />
    </div>
  );
}
